import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { toCents } from "@/lib/format";
import { Amount } from "@/components/common/Amount";
import { PRIORIDADES } from "@/lib/wo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ServicePicker } from "@/components/common/ServicePicker";
import { PropertyPicker } from "@/components/common/PropertyPicker";

const TURNOVER_LINES = [
  "Pintura interior completa (paredes, techos y marcos)",
  "Limpieza profunda de unidad (cocina, baños, pisos y ventanas)",
  "Revisión y reparación de detalles menores (drywall, herrajes, tapas)",
];

const emptyLine = () => ({ descripcion: "", costo: "", precio: "" });

// Fix K (2026-09-28): las etiquetas de categoría viven ahora en components/common/ServicePicker.jsx.

export const WorkOrderDialog = ({ open, onOpenChange, turnover = false }) => {
  const { t } = useT();
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [propertyId, setPropertyId] = useState("");
  const [serviceId, setServiceId] = useState("");
  // Fix K (2026-09-28): el precio al cliente se precarga con la tarifa y se puede editar. Si el
  // usuario lo escribe a mano, no se le vuelve a pisar hasta que cambie edificio o servicio.
  const [priceTouched, setPriceTouched] = useState(false);
  const [unitText, setUnitText] = useState("");
  const [unitId, setUnitId] = useState("");
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [prioridad, setPrioridad] = useState("media");
  const [dueAt, setDueAt] = useState("");
  const [responsable, setResponsable] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [clientPrice, setClientPrice] = useState("");
  const [motivoPrecio, setMotivoPrecio] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const { data: properties } = useQuery({
    queryKey: ["properties-options-all"],
    enabled: open,
    queryFn: async () => (await api.get("/properties", { params: { page_size: 200 } })).data,
  });
  const { data: services } = useQuery({
    queryKey: ["service-types"],
    enabled: open,
    queryFn: async () => (await api.get("/service-types")).data,
  });
  const { data: units } = useQuery({
    queryKey: ["units-of", propertyId],
    enabled: open && !!propertyId,
    queryFn: async () =>
      (await api.get("/units", { params: { property_id: propertyId, page_size: 500 } })).data,
  });
  const { data: operational } = useQuery({
    queryKey: ["settings-operational"],
    enabled: open,
    queryFn: async () => (await api.get("/settings/operational")).data,
  });
  const { data: users } = useQuery({
    queryKey: ["users-options"],
    enabled: open && can("users", "read"),
    queryFn: async () => (await api.get("/users")).data,
  });
  const propertyBrand = (properties?.items || []).find((p) => p.id === propertyId)?.brand;
  const { data: rates } = useQuery({
    // Fix G (2026-09-03): sin el edificio, la pantalla mostraba el precio general mientras el
    // backend guardaba el del edificio. El precio de referencia tiene que ser el mismo que se
    // va a grabar en la orden.
    queryKey: ["rate-cards", propertyBrand, propertyId],
    enabled: open && !!propertyBrand,
    queryFn: async () => (await api.get("/rate-cards", {
      params: { brand: propertyBrand, property_id: propertyId || undefined },
    })).data,
  });

  useEffect(() => {
    if (!open) return;
    setLines(turnover ? TURNOVER_LINES.map((d) => ({ ...emptyLine(), descripcion: d })) : [emptyLine()]);
    setPropertyId("");
    setServiceId("");
    setUnitText("");
    setUnitId("");
    setTitulo("");
    setDescripcion("");
    setPrioridad("media");
    setDueAt("");
    setFiles([]);
    setResponsable(user?.id || "");
    setClientPrice("");
    setPriceTouched(false);
    setMotivoPrecio("");
  }, [open, turnover, user]);

  const property = useMemo(
    () => (properties?.items || []).find((p) => p.id === propertyId),
    [properties, propertyId]
  );

  // Fix J (2026-09-18): qué servicios tienen precio para el edificio elegido. `rates` ya trae la
  // lista general y la del edificio (fix G), así que el conjunto de service_type_id que aparecen
  // ahí ES la respuesta; no hace falta pedir nada más al backend. Con el edificio sin elegir el
  // conjunto queda vacío y no se marca nada, que es lo correcto: todavía no se sabe.
  const serviciosConTarifa = useMemo(
    () => new Set((rates?.items || []).map((r) => r.service_type_id)),
    [rates]
  );

  // Fix K (2026-09-28): el agrupado por categoría y el orden «con tarifa primero» viven ahora en
  // components/common/ServicePicker.jsx, que comparten «Nueva orden» e Intake.

  const tarifaRef = useMemo(() => {
    const delServicio = (rates?.items || []).filter((r) => r.service_type_id === serviceId);
    // Fix G: el endpoint devuelve la general y la del edificio; la del edificio pisa.
    return delServicio.find((r) => r.origen === "edificio") || delServicio[0];
  }, [rates, serviceId]);
  const excedePrecio = tarifaRef && clientPrice && toCents(clientPrice) !== tarifaRef.precio_cliente_cents;

  // Fix K (2026-09-28): antes la tarifa quedaba solo como texto de ejemplo y el campo vacío, así
  // que parecía que había que escribir el precio igual. Ahora queda cargada como valor real.
  useEffect(() => {
    if (priceTouched) return;
    setClientPrice(tarifaRef ? (tarifaRef.precio_cliente_cents / 100).toFixed(2) : "");
  }, [tarifaRef, priceTouched]);

  useEffect(() => {
    if (turnover && services?.items?.length && !serviceId) {
      const st = services.items.find((s) => s.nombre === "Turnover completo");
      if (st) setServiceId(st.id);
    }
  }, [turnover, services, serviceId]);

  useEffect(() => {
    const horas = operational?.vencimiento_horas?.[prioridad];
    if (!horas) return;
    const d = new Date(Date.now() + horas * 3600000);
    setDueAt(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
  }, [prioridad, operational]);

  const unitSuggestions = useMemo(() => {
    const list = units?.items || [];
    if (!unitText) return list.slice(0, 6);
    return list.filter((u) => u.etiqueta.toLowerCase().includes(unitText.toLowerCase())).slice(0, 6);
  }, [units, unitText]);

  const submit = async (thenAssign) => {
    if (!propertyId || !serviceId) {
      toast.error(t("app.required"));
      return;
    }
    if (excedePrecio && motivoPrecio.trim().length < 5) {
      toast.error(t("wo.form.price_reason"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        property_id: propertyId,
        unit_id: unitId || undefined,
        unit_nueva: !unitId && unitText ? unitText : undefined,
        service_type_id: serviceId,
        titulo: titulo || undefined,
        descripcion: descripcion || undefined,
        prioridad,
        responsable_user_id: responsable || undefined,
        due_at: dueAt ? new Date(dueAt).toISOString() : undefined,
        client_price_cents: clientPrice ? toCents(clientPrice) : undefined,
        motivo_precio: motivoPrecio.trim() || undefined,
        lineas: lines
          .filter((l) => l.descripcion.trim())
          .map((l) => ({
            descripcion: l.descripcion.trim(),
            sub_cost_cents: toCents(l.costo),
            client_price_cents: toCents(l.precio),
          })),
      };
      const { data } = await api.post("/work-orders", payload);
      const id = data.wo.id;
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        form.append("tipo", "antes");
        await api.post(`/work-orders/${id}/evidence`, form);
      }
      qc.invalidateQueries({ queryKey: ["work-orders"] });
      qc.invalidateQueries({ queryKey: ["wo-summary"] });
      toast.success(t("wo.form.created"));
      onOpenChange(false);
      navigate(`/ordenes/${id}${thenAssign ? "?assign=1" : ""}`);
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setSaving(false);
    }
  };

  const totalCosto = lines.reduce((a, l) => a + toCents(l.costo), 0);
  const totalPrecio = lines.reduce((a, l) => a + toCents(l.precio), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="wo-dialog"
        className="max-h-[92vh] max-w-[820px] overflow-y-auto rounded-surface"
      >
        <DialogHeader>
          <DialogTitle className="text-[16px]">
            {turnover ? t("wo.form.turnover_title") : t("wo.form.title")}
          </DialogTitle>
          <DialogDescription className="text-[13px]">{t("wo.subtitle")}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3.5 sm:grid-cols-2">
          {/* Fix K (2026-09-28): el buscador de edificios es el mismo componente que usa Intake. */}
          <div>
            <Label className="text-[12.5px]">{t("wo.form.property")}</Label>
            <PropertyPicker
              cargando={!properties}
              properties={properties?.items || []}
              value={propertyId}
              testId="wo-form-property"
              onChange={(id) => { setPropertyId(id); setPriceTouched(false); }}
            />
            {property && (
              <p className="mt-1 text-[11.5px] text-ink-secondary" data-testid="wo-form-autofill">
                {property.company_nombre} · {property.brand === "bb" ? "B&B" : "Betos"} ·{" "}
                {t("wo.form.auto")}
              </p>
            )}
            {property && (
              <p className="mt-0.5 text-[11.5px] text-ink-secondary" data-testid="wo-form-address">
                {[property.direccion?.calle, property.direccion?.ciudad,
                  property.direccion?.estado, property.direccion?.zip]
                  .filter(Boolean).join(", ") || t("wo.form.no_address")}
              </p>
            )}
          </div>

          <div className="relative">
            <Label className="text-[12.5px]">{t("wo.form.unit")}</Label>
            <Input
              data-testid="wo-form-unit"
              value={unitText}
              disabled={!propertyId}
              onChange={(e) => {
                setUnitText(e.target.value);
                setUnitId("");
              }}
              placeholder={t("wo.form.unit_ph")}
              className="mt-1 h-9 text-[13px]"
            />
            {!!unitText && !unitId && !!unitSuggestions.length && (
              <ul className="absolute z-30 mt-1 w-full overflow-hidden rounded-surface border border-line bg-surface shadow-md">
                {unitSuggestions.map((u) => (
                  <li key={u.id}>
                    <button
                      type="button"
                      data-testid={`wo-form-unit-option-${u.etiqueta}`}
                      onClick={() => {
                        setUnitId(u.id);
                        setUnitText(u.etiqueta);
                      }}
                      className="block w-full px-3 py-1.5 text-left text-[13px] hover:bg-surface-sunken"
                    >
                      {u.etiqueta}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Fix K (2026-09-28): primero el tipo de trabajo (turnover / proyecto / remediation),
              después el servicio con buscador. Si no existe, se da de alta desde acá mismo. */}
          <div>
            <Label className="text-[12.5px]">{t("wo.form.service")}</Label>
            <ServicePicker
              services={services?.items || []}
              value={serviceId}
              testId="wo-form-service"
              conTarifa={propertyId ? serviciosConTarifa : null}
              onChange={(id) => { setServiceId(id); setPriceTouched(false); }}
            />
          </div>

          <div>
            <Label className="text-[12.5px]">{t("wo.form.price")}</Label>
            <Input
              data-testid="wo-form-price"
              value={clientPrice}
              inputMode="decimal"
              placeholder="0.00"
              onChange={(e) => { setClientPrice(e.target.value); setPriceTouched(true); }}
              className="mt-1 h-9 text-[13px]"
            />
            {tarifaRef ? (
              <p className="mt-1 text-[11.5px] text-ink-secondary" data-testid="wo-form-rate-reference">
                {t("wo.form.rate_reference", { amount: `$${(tarifaRef.precio_cliente_cents / 100).toFixed(2)}` })}
                {tarifaRef.origen === "edificio" && ` · ${t("wo.form.rate_from_building")}`}
              </p>
            ) : propertyId && serviceId ? (
              // Fix J (2026-09-18): el precio vacío tiene una causa concreta y hay que decirla.
              <p className="mt-1 text-[11.5px] text-state-warn" data-testid="wo-form-no-rate">
                {t("wo.form.no_rate_hint")}
              </p>
            ) : (
              <p className="mt-1 text-[11.5px] text-ink-secondary">{t("wo.form.price_hint")}</p>
            )}
          </div>

          {excedePrecio && (
            <div className="sm:col-span-2">
              <Label className="text-[12.5px] text-state-critical">{t("wo.form.price_reason")}</Label>
              <Textarea
                data-testid="wo-form-price-reason"
                value={motivoPrecio}
                onChange={(e) => setMotivoPrecio(e.target.value)}
                placeholder={t("wo.form.price_reason_ph")}
                rows={2}
                className="mt-1 text-[13px]"
              />
            </div>
          )}

          <div>
            <Label className="text-[12.5px]">{t("wo.form.priority")}</Label>
            <Select value={prioridad} onValueChange={setPrioridad}>
              <SelectTrigger data-testid="wo-form-priority" className="mt-1 h-9 text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORIDADES.map((p) => (
                  <SelectItem key={p} value={p}>{t(`priority.${p}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[12.5px]">{t("wo.form.due")}</Label>
            <Input
              type="datetime-local"
              data-testid="wo-form-due"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className="mt-1 h-9 text-[13px]"
            />
            <p className="mt-1 text-[11.5px] text-ink-secondary">{t("wo.form.due_hint")}</p>
          </div>

          {can("users", "read") && (
            <div>
              <Label className="text-[12.5px]">{t("wo.form.responsable")}</Label>
              <Select value={responsable} onValueChange={setResponsable}>
                <SelectTrigger data-testid="wo-form-responsable" className="mt-1 h-9 text-[13px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(users?.items || []).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="sm:col-span-2">
            <Label className="text-[12.5px]">{t("wo.form.titulo")}</Label>
            <Input
              data-testid="wo-form-title"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder={(services?.items || []).find((s) => s.id === serviceId)?.nombre || ""}
              className="mt-1 h-9 text-[13px]"
            />
          </div>

          <div className="sm:col-span-2">
            <Label className="text-[12.5px]">{t("wo.form.descripcion")}</Label>
            <Textarea
              data-testid="wo-form-description"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={2}
              className="mt-1 text-[13px]"
            />
          </div>
        </div>

        <div className="border border-line">
          <div className="flex items-center justify-between border-b border-line bg-surface-sunken px-3 py-2">
            <span className="text-[12.5px] font-semibold text-ink">{t("wo.form.scope")}</span>
            <span className="flex items-center gap-1 font-mono text-[11.5px] tabular-nums text-ink-secondary">
              <Amount cents={totalCosto} className="text-[11.5px]" /> / <Amount cents={totalPrecio} className="text-[11.5px]" />
            </span>
          </div>
          <div className="space-y-2 p-3">
            {lines.map((l, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  data-testid={`wo-form-line-${i}`}
                  value={l.descripcion}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...l, descripcion: e.target.value };
                    setLines(next);
                    if (i === lines.length - 1 && e.target.value) setLines([...next, emptyLine()]);
                  }}
                  placeholder={t("wo.form.line_ph")}
                  className="h-9 flex-1 text-[13px]"
                />
                <Input
                  data-testid={`wo-form-line-cost-${i}`}
                  value={l.costo}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...l, costo: e.target.value };
                    setLines(next);
                  }}
                  placeholder={t("wo.form.line_cost")}
                  className="h-9 w-[92px] text-right text-[13px]"
                />
                <Input
                  data-testid={`wo-form-line-price-${i}`}
                  value={l.precio}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...l, precio: e.target.value };
                    setLines(next);
                  }}
                  placeholder={t("wo.form.line_price")}
                  className="h-9 w-[92px] text-right text-[13px]"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`wo-form-line-remove-${i}`}
                  onClick={() => setLines(lines.length > 1 ? lines.filter((_, idx) => idx !== i) : [emptyLine()])}
                  className="h-9 w-9 shrink-0 p-0 text-ink-tertiary hover:text-state-critical"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              data-testid="wo-form-add-line"
              onClick={() => setLines([...lines, emptyLine()])}
              className="h-8 text-[12.5px]"
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> {t("wo.form.add_line")}
            </Button>
          </div>
        </div>

        <div>
          <Label className="text-[12.5px]">{t("wo.form.photos")}</Label>
          <div className="mt-1 flex items-center gap-3">
            <label className="inline-flex h-9 cursor-pointer items-center gap-2 border border-line px-3 text-[13px] hover:border-line-strong">
              <ImageIcon className="h-3.5 w-3.5 text-ink-secondary" />
              {t("wo.form.photos")}
              <input
                type="file"
                multiple
                accept="image/*,.heic,.heif"
                data-testid="wo-form-photos"
                className="hidden"
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
              />
            </label>
            <span className="text-[11.5px] text-ink-secondary">
              {files.length ? `${files.length} archivo(s)` : t("wo.form.photos_hint")}
            </span>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-9 text-[13px]">
            {t("app.cancel")}
          </Button>
          <Button
            variant="outline"
            data-testid="wo-form-save-assign"
            disabled={saving}
            onClick={() => submit(true)}
            className="h-9 text-[13px]"
          >
            {t("wo.form.save_assign")}
          </Button>
          <Button
            data-testid="wo-form-save"
            disabled={saving}
            onClick={() => submit(false)}
            className="h-9 text-[13px]"
          >
            {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {t("wo.form.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
